/**
 * Servicio: Catálogo de Países
 * Endpoint: /wspais/get
 *
 * Gestiona la consulta de países con caché de 24 horas
 */

import { sendRequest } from '../../config';
import { getFromCache, saveToCache, normalizeKeys, filterInMemory } from '../../utils/cacheHelper';

// ============================================================================
// TIPOS
// ============================================================================

export interface Pais {
  cpais: string;
  xpais: string;
  [key: string]: any;
}

export interface GetPaisesParams {
  cpais?: string;
  [key: string]: any;
}

// ============================================================================
// SERVICIO
// ============================================================================

/**
 * Obtiene la lista de países
 *
 * **Estrategia de caché:**
 * 1. Intenta obtener del localStorage (24h TTL)
 * 2. Si no existe o expiró, consulta el WS SIN FILTROS
 * 3. Guarda TODO en localStorage
 * 4. Filtra en memoria según parámetros
 *
 * @param params - Filtros opcionales (cpais, etc.)
 * @returns Array de países
 *
 * @example
 * ```ts
 * // Obtener todos los países
 * const paises = await getPaises();
 *
 * // Obtener país específico
 * const venezuela = await getPaises({ cpais: '2' });
 * ```
 */
export const getPaises = async (
  params?: GetPaisesParams
): Promise<Pais[]> => {
  const CACHE_KEY = 'catalogo_paises';
  const ENDPOINT = 'wspais/get';

  // 1. Intentar obtener del cache
  let allData = getFromCache<Pais>(CACHE_KEY);

  // 2. Si no hay cache, consultar WS
  if (!allData) {
    const response = await sendRequest<Pais | Pais[]>({
      method: 'post',
      path: ENDPOINT,
      body: {}, // Sin filtros para traer TODOS
    });

    // Normalizar respuesta a array
    const rawData = Array.isArray(response) ? response : (response ? [response] : []);
    allData = normalizeKeys<Pais>(rawData);

    // 3. Guardar en cache
    saveToCache(CACHE_KEY, allData);
  }

  // 4. Filtrar en memoria según parámetros
  return filterInMemory(allData, params);
};
